import React, { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';

import Swipe, { SwipeItem } from 'swipejs/react';

import Metrics from '@spotahome/soyuz-metrics';
import { trans, getResourcePath } from '@spotahome/soyuz/client';
import { Modal, ModalHeader, CircleCheckedIcon } from '@spotahome/ui-library';

import { COOKIE_VALUE_PROPOSITION_MODAL } from '../../constants';

import BulletPoints from './BulletPoints';

import './ValuePropositionModal.scss';
import './Swipe.scss';

const expireNineMonthsFromNow = () => {
  const timestamp = new Date().getTime();
  const expiration = timestamp + 1000 * 60 * 60 * 24 * 30 * 9;
  return new Date(expiration);
};

const MODAL_STEPS = [
  {
    title: 'search.value-proposition-modal.step1-title',
    renderMedia: () => (
      <picture className="value-proposition-modal__images-picture">
        <img
          alt={trans('search.value-proposition-modal.step1-title')}
          src={getResourcePath('/images/value-proposition/modal-step-1.jpg')}
        />
        <div className="value-proposition-modal__images-first">
          {trans('search.value-proposition-modal.step1.image-text')}
        </div>
      </picture>
    )
  },
  {
    title: 'search.value-proposition-modal.step2-title',
    renderMedia: () => (
      <video
        className="value-proposition-modal__images-video"
        data-test="value-proposition-modal-animation"
        muted
        loop
        playsInline
        preload="auto"
        autoPlay
      >
        <source
          src={getResourcePath('/videos/VP-modal.mp4')}
          type="video/webm"
        />
      </video>
    )
  },
  {
    title: 'search.value-proposition-modal.step3-title',
    renderMedia: () => (
      <picture className="value-proposition-modal__images-picture">
        <img
          alt={trans('search.value-proposition-modal.step3-title')}
          src={getResourcePath('/images/value-proposition/modal-step-3.jpg')}
        />
        <div className="value-proposition-modal__images-third">
          {trans('search.value-proposition-modal.step3.image-text')}
        </div>
      </picture>
    )
  },
  {
    title: 'search.value-proposition-modal.step4-title',
    renderMedia: () => (
      <picture className="value-proposition-modal__images-picture">
        <img
          alt={trans('search.value-proposition-modal.step4-title')}
          src={getResourcePath('/images/value-proposition/modal-step-4.jpg')}
        />
        <div className="value-proposition-modal__images-forth">
          <CircleCheckedIcon className="value-proposition-modal__images-forth-icon" />
          {trans('search.value-proposition-modal.step4.image-text')}
        </div>
      </picture>
    )
  }
];

const TOTAL_MODAL_STEPS = MODAL_STEPS.length;

const ValuePropositionModal = () => {
  const [stepNumber, setStepNumber] = useState(1);
  const [isModalOpen, setModalIsOpen] = useState(true);

  const cookies = new Cookies();
  let swipeSlider;

  useEffect(
    () =>
      cookies.set(COOKIE_VALUE_PROPOSITION_MODAL, true, {
        path: '/',
        expires: expireNineMonthsFromNow()
      }),
    [cookies]
  );

  const isFirstStep = () => stepNumber === 1;
  const isLastStep = () => stepNumber === TOTAL_MODAL_STEPS;

  const decreaseModalStep = () => {
    if (stepNumber > 1) {
      swipeSlider.prev();
      setStepNumber(stepNumber - 1);
    }
  };

  const increaseModalStep = () => {
    if (stepNumber < TOTAL_MODAL_STEPS) {
      setStepNumber(stepNumber + 1);
      swipeSlider.next();
      Metrics.ga.sendEvent(
        'Search',
        'value-proposition-modal_next-step',
        stepNumber
      );
    }
  };

  const handleModalClose = () => {
    Metrics.ga.sendEvent('Search', 'value-proposition-modal_close', 'close');
    setModalIsOpen(false);
  };

  const goToStep = desiredStep => {
    swipeSlider.slide(desiredStep);
    setStepNumber(desiredStep);
  };

  return (
    <Modal
      size="big"
      className="value-proposition-modal"
      isOpen={isModalOpen}
      onRequestClose={handleModalClose}
    >
      <ModalHeader
        className="value-proposition-modal__header"
        onRequestClose={handleModalClose}
        data-test="value-proposition-modal-close-btn"
      />

      <Swipe
        // eslint-disable-next-line no-return-assign
        ref={sliderRef => (swipeSlider = sliderRef)}
        auto={0}
        continuous={false}
        startSlide={0}
        speed={300}
        callback={index => setStepNumber(index + 1)}
        draggable
      >
        {MODAL_STEPS.map(step => (
          <SwipeItem>
            <div>
              <div className="value-proposition-modal__images">
                {step.renderMedia()}
              </div>
              <div
                className="value-proposition-modal__content"
                data-test="valuePropositionModal"
              >
                <h1 className="value-proposition-modal__content-title">
                  {trans(step.title)}
                </h1>
              </div>
            </div>
          </SwipeItem>
        ))}
      </Swipe>

      <div className="value-proposition-modal__content-footer">
        <button
          type="button"
          className="value-proposition-modal__content-cta value-proposition-modal__content-cta--prev"
          onClick={decreaseModalStep}
        >
          {isFirstStep() ? '' : trans('search.value-proposition.previous')}
        </button>

        <div className="value-proposition-modal__content-bullets-container">
          <BulletPoints
            totalSteps={TOTAL_MODAL_STEPS}
            currentStep={stepNumber}
            handleOnClick={goToStep}
          />
        </div>

        <button
          type="button"
          className="value-proposition-modal__content-cta value-proposition-modal__content-cta--next"
          onClick={isLastStep() ? handleModalClose : increaseModalStep}
        >
          {isLastStep()
            ? trans('search.value-proposition.start')
            : trans('search.value-proposition.next')}
        </button>
      </div>
    </Modal>
  );
};

export default ValuePropositionModal;
